<template>
    <!-- Full-height review workspace: its week-range header, grouped cards and planning action share a continuous themed surface. -->
    <BaseDialog
        v-model:open="open"
        layout="custom"
        :title="t('developmentJourneys.weeklyActionReview.title')"
        :description="t('developmentJourneys.weeklyActionReview.description')"
        disable-close-on-backdrop-click
        overlay-class="fixed"
        dialog-content-class="fixed top-4 right-4 bottom-4 left-4 w-auto max-h-none max-w-[38.75rem] overflow-hidden rounded-xl p-0 pb-0 md:top-1/2 md:right-auto md:bottom-auto md:left-1/2 md:w-[calc(100%-2rem)] md:max-h-[80dvh]">
        <section data-testid="weekly-action-review" class="flex h-full min-h-0 flex-col overflow-hidden md:max-h-[80dvh]">
            <div class="journeys-review-dialog-header flex shrink-0 items-center gap-3 border-b border-border px-5 py-5 sm:px-6">
                <DevelopmentJourneyHourglass class="size-7 shrink-0" />
                <div class="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h2 class="journeys-review-dialog-header__title">{{ t("developmentJourneys.weeklyActionReview.heading") }}</h2>
                    <span class="journeys-review-dialog-header__range">{{ weekRange }}</span>
                </div>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto bg-surface-sunken">
                <div class="journeys-review-action-list flex flex-col" :aria-label="t('developmentJourneys.weeklyActionReview.actionsFromLastWeekAria')">
                    <section v-if="completedActionGroups.length > 0" class="journeys-review-completed-section flex flex-col" aria-labelledby="review-completed-actions-title">
                        <h3 id="review-completed-actions-title" class="journeys-review-completed-section__title">{{ t("developmentJourneys.weeklyActionReview.completed") }}</h3>
                        <div v-for="group in completedActionGroups" :key="group.key" class="flex flex-col gap-2.5">
                            <h4 class="journeys-review-completed-section__group flex items-center gap-1">
                                <span class="size-2 shrink-0 rounded-full bg-journey-marker" aria-hidden="true"></span>
                                {{ group.title }}
                            </h4>
                            <ul class="flex flex-col gap-2.5">
                                <li v-for="action in group.actions" :key="action.id">
                                    <WeeklyActionCard
                                        :action="action"
                                        :role-play-setup-url="rolePlaySetupUrl"
                                        variant="review"
                                        :review-outcome="outcomes[action.id]"
                                        :timezone="timezone"
                                        :review-disabled="isSubmitting"
                                        @set-review-outcome="setOutcome"
                                        @view-details="(actionId) => emit('view-details', actionId)" />
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section
                        v-for="group in activeActionGroups"
                        :key="group.key"
                        class="journeys-review-action-group flex flex-col"
                        :aria-labelledby="`review-action-group-${group.key}`">
                        <h4 :id="`review-action-group-${group.key}`" class="journeys-review-action-group__label flex items-center gap-1">
                            <span class="size-2 shrink-0 rounded-full bg-journey-marker" aria-hidden="true"></span>
                            {{ group.title }}
                        </h4>
                        <ul class="flex flex-col gap-2.5">
                            <li v-for="action in group.actions" :key="action.id">
                                <WeeklyActionCard
                                    :action="action"
                                    :role-play-setup-url="rolePlaySetupUrl"
                                    variant="review"
                                    :review-outcome="outcomes[action.id]"
                                    :timezone="timezone"
                                    :review-disabled="isSubmitting"
                                    @set-review-outcome="setOutcome"
                                    @view-details="(actionId) => emit('view-details', actionId)" />
                            </li>
                        </ul>
                    </section>
                </div>
            </div>

            <div class="flex shrink-0 justify-end bg-surface-sunken px-5 pb-5 pt-3 sm:px-6 sm:pb-6">
                <JourneyButton data-testid="continue-weekly-action-review" theme="primary" size="md" :disabled="isSubmitting" :aria-busy="isSubmitting" @click="submitReview">
                    {{ isSubmitting ? t("common.saving") : t("developmentJourneys.weeklyActionReview.saveAndPlan") }}
                </JourneyButton>
            </div>
        </section>
    </BaseDialog>
</template>

<script setup lang="ts">
import type { DevelopmentActionReviewOutcome } from "~vue/api/generated/development-journeys";
import BaseDialog from "~vue/components/BaseDialog.vue";
import DevelopmentJourneyHourglass from "~vue/icons/DevelopmentJourneyHourglass.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import WeeklyActionCard from "./WeeklyActionCard.vue";
import type { WeeklyAction } from "./weeklyActions";

const props = defineProps<{
    actions: WeeklyAction[];
    rolePlaySetupUrl: string;
    weekRange: string;
    timezone?: string;
    isSubmitting: boolean;
}>();

const open = defineModel<boolean>("open", { default: false });

const emit = defineEmits<{
    submit: [outcomes: DevelopmentActionReviewOutcome[]];
    "view-details": [actionId: string];
    "update:reviewed-action-count": [count: number];
}>();

type ReviewOutcome = DevelopmentActionReviewOutcome["status"];
type ActionGroup = { key: string; title: string; actions: WeeklyAction[] };

const { t } = useI18n();

const outcomes = ref<Record<string, ReviewOutcome>>({});
const reviewedActionCount = computed(() => Object.keys(outcomes.value).length);
const activeActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => outcomes.value[action.id] !== "completed")));
const completedActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => outcomes.value[action.id] === "completed")));

watch(
    () => props.actions.map((action) => `${action.id}:${action.status}`).join(","),
    () => {
        outcomes.value = Object.fromEntries(props.actions.filter((action) => action.status === "completed").map((action) => [action.id, "completed"]));
    },
    { immediate: true },
);

watch(reviewedActionCount, (count) => emit("update:reviewed-action-count", count), { immediate: true });

const setOutcome = (actionId: string, outcome: ReviewOutcome) => {
    if (props.isSubmitting) {
        return;
    }
    outcomes.value = { ...outcomes.value, [actionId]: outcome };
};

const submitReview = () => {
    if (props.isSubmitting) {
        return;
    }

    emit(
        "submit",
        props.actions.map((action) => ({ action_id: action.id, status: outcomes.value[action.id] ?? "not_completed" })),
    );
};

function groupActions(actions: WeeklyAction[]): ActionGroup[] {
    const groups = new Map<string, ActionGroup>();
    for (const action of actions) {
        const goal = action.development_goal;
        const key = goal?.id ?? "independent-actions";
        const title = goal?.title ?? t("developmentJourneys.page.actionsTitle");
        const group = groups.get(key) ?? { key, title, actions: [] };
        group.actions.push(action);
        groups.set(key, group);
    }
    return [...groups.values()];
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .journeys-review-action-list {
        gap: 20px;
        padding: 20px;
    }

    .journeys-review-dialog-header__title {
        @apply nadia-subtitle text-ink;
    }

    .journeys-review-dialog-header__range {
        @apply body-small text-ink-muted;
    }

    .journeys-review-action-group {
        gap: 4px;
    }

    .journeys-review-action-group + .journeys-review-action-group {
        margin-top: 20px;
    }

    .journeys-review-action-group__label,
    .journeys-review-completed-section__title,
    .journeys-review-completed-section__group {
        @apply scaffold-details-small text-ink-muted;
    }

    .journeys-review-completed-section {
        gap: 10px;
    }
}
</style>
